import { useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { ReleaseType } from '@ibk/shared-types';
import { formatCurrency } from '@ibk/shared-utils';
import { useGetBusinessDaysQuery } from '../../../services/benefitApi';
export function useSummaryData(formData, product, simulationResult, onBusinessDaysChange) {
    const shouldFetchBusinessDays = useMemo(() => (formData.releaseType === ReleaseType.MONTHLY || formData.releaseType === ReleaseType.DAILY) &&
        formData.competenceDate &&
        !formData.businessDays, [formData.releaseType, formData.competenceDate, formData.businessDays]);
    const competenceFormatted = useMemo(() => (formData.competenceDate ? format(formData.competenceDate, 'yyyy-MM') : ''), [formData.competenceDate]);
    const { data: businessDaysData, isLoading: isLoadingBusinessDays } = useGetBusinessDaysQuery({ competence: competenceFormatted }, { skip: !shouldFetchBusinessDays || !competenceFormatted });
    const businessDays = formData.businessDays ?? businessDaysData?.businessDays ?? null;
    useEffect(() => {
        if (businessDays !== null &&
            businessDays !== undefined &&
            formData.businessDays === undefined &&
            onBusinessDaysChange &&
            !isLoadingBusinessDays) {
            onBusinessDaysChange(businessDays);
        }
    }, [businessDays, formData.businessDays, onBusinessDaysChange, isLoadingBusinessDays]);
    return useMemo(() => {
        const totalValue = calculateTotalValue(formData, businessDays ?? undefined, product, simulationResult);
        return {
            totalValue,
        };
    }, [formData, businessDays, product, simulationResult]);
}
function calculateIndividualBeneficiariesTotal(formData) {
    if (!formData.beneficiaries?.length)
        return null;
    let total = 0;
    let hasIndividualValues = false;
    formData.beneficiaries.forEach(beneficiary => {
        if (beneficiary.productValue && beneficiary.productValue > 0) {
            total += beneficiary.productValue;
            hasIndividualValues = true;
        }
    });
    if (!hasIndividualValues)
        return null;
    return formatCurrency(total);
}
function calculateGlobalValueTotal(formData, selectedBeneficiaries, businessDays) {
    if (!formData.globalValue)
        return null;
    const valueString = formData.globalValue.replace(/[^0-9,]/g, '').replace(',', '.');
    const valuePerBeneficiary = parseFloat(valueString) || 0;
    if (formData.releaseType === ReleaseType.MONTHLY) {
        const total = valuePerBeneficiary * selectedBeneficiaries;
        return `R$ ${total.toFixed(2).replace('.', ',')}`;
    }
    if (formData.releaseType === ReleaseType.DAILY && businessDays) {
        const monthlyValuePerBeneficiary = valuePerBeneficiary * businessDays;
        const total = monthlyValuePerBeneficiary * selectedBeneficiaries;
        return `R$ ${total.toFixed(2).replace('.', ',')}`;
    }
    return null;
}
function calculateTotalValue(formData, businessDays, product, simulationResult) {
    const selectedBeneficiaries = formData.selectedBeneficiaries ?? 0;
    if (selectedBeneficiaries === 0) {
        return 'R$ 0,00';
    }
    if (simulationResult?.amount) {
        return formatCurrency(simulationResult.amount);
    }
    if (formData.totalAmount) {
        return formData.totalAmount;
    }
    const individualTotal = calculateIndividualBeneficiariesTotal(formData);
    if (individualTotal) {
        return individualTotal;
    }
    const globalValueTotal = calculateGlobalValueTotal(formData, selectedBeneficiaries, businessDays);
    if (globalValueTotal) {
        return globalValueTotal;
    }
    const amountDaily = formData.amountDaily ?? product?.amountDaily;
    if (amountDaily && businessDays) {
        const monthlyValuePerBeneficiary = amountDaily * businessDays;
        const total = monthlyValuePerBeneficiary * selectedBeneficiaries;
        return `R$ ${total.toFixed(2).replace('.', ',')}`;
    }
    return 'R$ 0,00';
}
